import { useEffect, useState } from "react";
import {
  Create,
  ReferenceInput,
  required,
  FormDataConsumer,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useDataProvider,
  useGetList,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CustomToolbar = ({ checkpoints }) => {
  const redirect = useRedirect(); // Hook to redirect to another page
  const theme = useTheme(); // Access the theme
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const backgroundColor = theme.palette.background.paper; // Default background color

  const handleCancel = () => {
    redirect('list', 'routes');
  };

  const handleSuccessCreate = async (data) => {
    try {
      if (checkpoints.length > 0) {
        checkpoints.forEach(async (checkpoint) => {
          const checkpointData = {
            checkpoint_id: checkpoint.id,
            order: checkpoint.order,
            route_id: data.id,
          };
          await dataProvider.create("routeCheckPointss", { data: checkpointData });
        });
        }

      notify('Route created successfully', { type: 'success' });
      refresh();
      redirect('list', 'routes');
    } catch (error) {
      console.error('Error creating Route:', error);
      notify('Error creating Route', { type: 'error' });
    }
  };

  return (
    <Toolbar sx={{ backgroundColor }}>
      <Box display="flex" justifyContent="flex-end" width="100%">
        <Button
          variant="text"
          onClick={handleCancel}
          sx={{
            color: theme.palette.common.white,
            borderColor: theme.palette.primary.main,
            marginRight: theme.spacing(1),
            textTransform: 'none',
            minWidth: '95px',
          }}
          size='small'
        >
          Cancel
        </Button>
        <SaveButton
          type='button'
          label='Create'
          size='small'
          icon={null}
          sx={{ textTransform: 'none', minWidth: '95px' }}
          mutationOptions={{ onSuccess: handleSuccessCreate }}
        />
      </Box>
    </Toolbar>
  );
};

const CheckpointList = ({ onChange, siteId }) => {
  // Fetch all checkpoints
  const { data: checkpoints = [], isLoading } = useGetList('checkpoints', { filter: { site_id: siteId }, pagination: { page: 1, perPage: 1000 } });
  const [routeCheckpoints, setRouteCheckpoints] = useState([]);

  useEffect(() => {
    onChange(routeCheckpoints);
  }, [routeCheckpoints]);

  const handleCheckpointChange = (selectedId) => {
    if (selectedId) {
      const selectedCheckpoint = checkpoints.find((cp) => cp.id === selectedId)
      const updatedRouteCheckpoints = [
        ...routeCheckpoints,
        {
          id: selectedId,
          name: selectedCheckpoint.name,
          order: routeCheckpoints.length
        }
      ]
      setRouteCheckpoints(updatedRouteCheckpoints)
    }
  };

  const availableCheckpoints = checkpoints.filter(cp => !routeCheckpoints.some(routeCheckpoint => routeCheckpoint.id === cp.id ))

  const handleDelete = (rcp) => {
    const updatedrcps = routeCheckpoints.filter((item) => item.id !== rcp.id);
    setRouteCheckpoints(updatedrcps.map((urcps, idx) => ({ ...urcps, order: idx })));
  };

  const moveUp = (rcp) => {
    if (rcp.order === 0) return;
    swapOrder(rcp, "up");
  };

  const moveDown = (rcp) => {
    if (rcp.order === routeCheckpoints.length - 1) return;
    swapOrder(rcp, "down");
  };

  const swapOrder = (rcp, orderStatus) => {
    if (orderStatus === "up") {
      const targetRcp = routeCheckpoints.find((item) => item.order === rcp.order - 1);
      setRouteCheckpoints([
        ...routeCheckpoints.filter((rcpItem) => rcpItem.id !== targetRcp.id && rcpItem.id !== rcp.id),
        {...rcp, order: rcp.order - 1},
        {...targetRcp, order: targetRcp.order + 1},
      ].sort((a, b) => a.order - b.order));
    } else if (orderStatus === "down") {
      const targetRcp = routeCheckpoints.find((item) => item.order === rcp.order + 1);
      setRouteCheckpoints([
        ...routeCheckpoints.filter((rcpItem) => rcpItem.id !== targetRcp.id && rcpItem.id !== rcp.id),
        {...rcp, order: rcp.order + 1},
        {...targetRcp, order: targetRcp.order - 1},
      ].sort((a, b) => a.order - b.order));
    }
  };

  if (isLoading) return <Typography variant="caption">Loading...</Typography>;

  return (
    <div style={{ width: '100%', overflowY: 'auto', maxHeight: '200px' }} >
      {routeCheckpoints.length > 0 && routeCheckpoints.map((rcp, index, array) => (
        <div
          key={index}
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            backgroundColor: 'rgb(30, 30, 46)',
            border: '1px solid rgb(120, 120, 120)',
            marginTop: '10px',
          }}
        >
          <div>{index + 1}</div>
          <div style={{ margin: 'auto', marginLeft: '10px' }}>{rcp.name}</div>
          <IconButton disabled={index === 0} onClick={() => moveUp(rcp)}>
            <KeyboardArrowUpIcon />
          </IconButton>
          <IconButton disabled={index === array.length - 1} onClick={() => moveDown(rcp)}>
            <KeyboardArrowDownIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(rcp)}>
            <DeleteIcon />
          </IconButton>
        </div>
      ))}
      {checkpoints.length > 0 && (
        <FormControl fullWidth>
          <InputLabel id="select-checkpoint">+ Add checkpoint</InputLabel>
          <Select
            labelId="select-checkpoint"
            value={''}
            onChange={(e) => handleCheckpointChange(e.target.value)}
          >
            {availableCheckpoints.map((cp, idx) => (
              <MenuItem value={cp.id} key={idx}>{cp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </div>
  );
};

const CreateRoute = () => {
  const [changedCheckpoints, setChangedCheckpoints] = useState([]);

  return (
    <Card variant='elevation' sx={{ width: '50%' }}>
      <CardContent>
        <Create actions={false}>
          <SimpleForm toolbar={<CustomToolbar checkpoints={changedCheckpoints} />}>
            <Typography variant="subtitle2">Details</Typography>
            <TextInput source="name" label='Name' fullWidth validate={[required('Name is required')]} />
            <ReferenceInput source="site_id" reference="sites" label="Site">
              <SelectInput optionText="name" fullWidth validate={[required('Site selection is required')]} />
            </ReferenceInput>
            <ReferenceInput source="route_type_id" reference="routeTypes" label="Route Type">
              <SelectInput optionText="name" fullWidth validate={[required('Route type selection is required')]} />
            </ReferenceInput>
            <Typography variant="subtitle2" gutterBottom>
              Checkpoint Selection
            </Typography>
            <FormDataConsumer>
            {({ formData }) => formData.site_id  && <CheckpointList onChange={setChangedCheckpoints} siteId={formData.site_id} /> }
            </FormDataConsumer>
          </SimpleForm>
        </Create>
      </CardContent>
    </Card>
  );
};

export default CreateRoute;
export { CreateRoute };